import { apiClient } from '@/lib/apiClient';

/**
 * Downloads a private file the same way every other request is made.
 *
 * Uploaded files are private (CLAUDE.md §11) — there is no public URL to put in
 * an `<a href>`, so the bytes are fetched with the access token, handed to the
 * browser as an object URL, and the URL is revoked immediately afterwards so
 * the blob does not leak for the life of the tab.
 *
 * The companion of `SecureImage`, which does the same thing for something that
 * has to be displayed rather than saved.
 */
/**
 * Saves a table the browser already holds, without asking the server for it.
 *
 * The rows on screen came from the API and are already scoped to this
 * institute, so writing the file here is one fewer endpoint to authorise and
 * the download is instant. Anything the server has to compute — a term's fee
 * ledger, a report card — stays a server file and goes through
 * `downloadPrivateFile`.
 *
 * Escaping is the whole of it: a subject called `Maths, Paper 1` or a teacher
 * whose name carries a quote would otherwise shift every later column. A
 * leading apostrophe is put in front of anything starting `= + - @` so a
 * spreadsheet reads it as text rather than running it as a formula.
 */
export function downloadCsv(fileName: string, rows: (string | number | null)[][]): void {
  const body = rows.map((row) => row.map(toCsvCell).join(',')).join('\r\n');

  // The BOM is what makes Excel open a UTF-8 file as UTF-8 — without it, a
  // name in Telugu or Hindi arrives as mojibake.
  const blob = new Blob([`﻿${body}`], { type: 'text/csv;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function toCsvCell(value: string | number | null): string {
  if (value === null) return '';

  const text = String(value);
  // A spreadsheet treats these as the start of a formula, which is how a CSV
  // becomes an attack on whoever opens it.
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;

  return `"${safe.replaceAll('"', '""')}"`;
}

export async function downloadPrivateFile(path: string, suggestedName: string): Promise<void> {
  saveBlob(await fetchPrivateFile(path), suggestedName);
}

/**
 * The bytes of a private file, fetched with the access token — for a screen
 * that shows the file before (or instead of) saving it. `FilePreviewModal`
 * reads it once and saves the same blob if asked, so the file is not fetched
 * twice.
 */
export async function fetchPrivateFile(path: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(path, { responseType: 'blob' });
  return response.data;
}

/** Hands a blob the page already holds to the browser as a download. */
export function saveBlob(blob: Blob, suggestedName: string): void {
  const objectUrl = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    // The server also sends `Content-Disposition`; this is what names the file
    // when the browser takes the object URL route instead.
    link.download = suggestedName;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
